import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Upload, Check, X, Car, Search, Image, ChevronLeft, ChevronRight, Trash2, RefreshCw, Download, Share } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function AdminMobilePhotos() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Register service worker and handle PWA install
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw-photos.js')
        .then((reg) => console.log('SW registered:', reg.scope))
        .catch((err) => console.log('SW registration failed:', err));
    }

    // Add manifest link dynamically - point to upload page
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.href = '/manifest-photos.json';
    } else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/manifest-photos.json';
      document.head.appendChild(link);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setMessage({ type: 'success', text: 'App installée!' });
    }
    setInstallPrompt(null);
  };

  // Fetch vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles?limit=500&include_offline=true`, { headers: getAuthHeader() });
      const data = Array.isArray(res.data) ? res.data : res.data.vehicles || [];
      // Sort by stock number
      data.sort((a, b) => (a.stock_number || '').localeCompare(b.stock_number || ''));
      setVehicles(data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Filter vehicles by search
  const filteredVehicles = vehicles.filter(v => {
    const query = searchQuery.toLowerCase();
    return (
      (v.stock_number || '').toLowerCase().includes(query) ||
      (v.make || '').toLowerCase().includes(query) ||
      (v.model || '').toLowerCase().includes(query) ||
      String(v.year).includes(query)
    );
  });

  // Handle photo capture/upload
  const handlePhotoCapture = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedVehicle) return;

    setUploading(true);
    setMessage(null);

    try {
      // Upload files
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const uploadRes = await axios.post(`${API}/upload/images`, formData, {
        headers: getAuthHeader()
      });

      const newUrls = uploadRes.data.uploaded.map(img => `${process.env.REACT_APP_BACKEND_URL}${img.url}`);
      
      // Update vehicle with new images
      const currentImages = selectedVehicle.images || [];
      const updatedImages = [...currentImages, ...newUrls];

      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
        images: updatedImages
      }, { headers: getAuthHeader() });

      // Update local state
      setSelectedVehicle(prev => ({ ...prev, images: updatedImages }));
      setVehicles(prev => prev.map(v => 
        v.id === selectedVehicle.id ? { ...v, images: updatedImages } : v
      ));

      setMessage({ type: 'success', text: `${files.length} photo(s) ajoutée(s)!` });
    } catch (err) {
      console.error('Upload error:', err);
      setMessage({ type: 'error', text: 'Erreur lors du téléversement' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // Remove image
  const removeImage = async (index) => {
    if (!selectedVehicle) return;
    
    const updatedImages = selectedVehicle.images.filter((_, i) => i !== index);
    
    try {
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
        images: updatedImages
      }, { headers: getAuthHeader() });

      setSelectedVehicle(prev => ({ ...prev, images: updatedImages }));
      setVehicles(prev => prev.map(v => 
        v.id === selectedVehicle.id ? { ...v, images: updatedImages } : v
      ));
      setMessage({ type: 'success', text: 'Photo supprimée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  // Move image
  const moveImage = async (fromIdx, toIdx) => {
    if (!selectedVehicle || toIdx < 0 || toIdx >= selectedVehicle.images.length) return;
    
    const newImages = [...selectedVehicle.images];
    const [moved] = newImages.splice(fromIdx, 1);
    newImages.splice(toIdx, 0, moved);
    
    try {
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
        images: newImages
      }, { headers: getAuthHeader() });

      setSelectedVehicle(prev => ({ ...prev, images: newImages }));
      setVehicles(prev => prev.map(v => 
        v.id === selectedVehicle.id ? { ...v, images: newImages } : v
      ));
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du réordonnancement' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white p-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="h-6 w-6" />
            <span className="font-bold text-lg">Photos Véhicules</span>
          </div>
          <div className="flex items-center gap-2">
            {installPrompt && !isInstalled && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleInstall}
                className="text-white hover:bg-white/20 text-xs"
              >
                <Download className="h-4 w-4 mr-1" />
                Installer
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchVehicles}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher par stock #, marque, modèle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-lg bg-white"
          />
        </div>
      </div>

      {/* Vehicle Selection */}
      {!selectedVehicle ? (
        <div className="px-4 pb-4">
          <p className="text-sm text-slate-500 mb-3">Sélectionnez un véhicule ({filteredVehicles.length})</p>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filteredVehicles.map(vehicle => (
              <button
                key={vehicle.id}
                onClick={() => setSelectedVehicle(vehicle)}
                className="w-full bg-white rounded-lg p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow text-left"
              >
                {/* Thumbnail */}
                <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                  {vehicle.images?.[0] ? (
                    <img 
                      src={vehicle.images[0]} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car className="h-6 w-6 text-slate-300" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {vehicle.stock_number || 'N/A'}
                    </Badge>
                    <Badge className={`text-[10px] ${
                      vehicle.images?.length > 0 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {vehicle.images?.length || 0} photos
                    </Badge>
                  </div>
                  <p className="font-semibold text-slate-900 truncate">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{vehicle.trim}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Selected Vehicle View */
        <div className="px-4 pb-24">
          {/* Back button and vehicle info */}
          <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            <button 
              onClick={() => setSelectedVehicle(null)}
              className="text-blue-600 text-sm flex items-center gap-1 mb-3"
            >
              <ChevronLeft className="h-4 w-4" /> Retour à la liste
            </button>
            
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono">
                {selectedVehicle.stock_number || 'N/A'}
              </Badge>
              <Badge className="bg-green-100 text-green-700">
                {selectedVehicle.images?.length || 0} photos
              </Badge>
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
            </h2>
            <p className="text-slate-500">{selectedVehicle.trim}</p>
          </div>

          {/* Photos Grid */}
          <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Image className="h-4 w-4" />
              Photos ({selectedVehicle.images?.length || 0})
            </h3>
            
            {selectedVehicle.images?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {selectedVehicle.images.map((url, idx) => (
                  <div key={url + idx} className="relative aspect-[4/3] group">
                    <img 
                      src={url} 
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="10">Erreur</text></svg>';
                      }}
                    />
                    {/* Position badge */}
                    <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded ${
                      idx === 0 ? 'bg-blue-600 text-white' : 'bg-black/60 text-white'
                    }`}>
                      {idx === 0 ? '★' : idx + 1}
                    </span>
                    {/* Controls */}
                    <div className="absolute bottom-1 right-1 flex gap-1">
                      {idx > 0 && (
                        <button
                          onClick={() => moveImage(idx, idx - 1)}
                          className="p-1 bg-white/90 rounded shadow"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                      )}
                      {idx < selectedVehicle.images.length - 1 && (
                        <button
                          onClick={() => moveImage(idx, idx + 1)}
                          className="p-1 bg-white/90 rounded shadow"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => removeImage(idx)}
                        className="p-1 bg-red-500 text-white rounded shadow"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucune photo</p>
                <p className="text-sm">Prenez des photos ci-dessous</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Camera Button - Fixed at bottom when vehicle selected */}
      {selectedVehicle && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 shadow-lg">
          {/* Hidden file inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handlePhotoCapture}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoCapture}
            className="hidden"
          />
          
          {/* Camera button */}
          <Button
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 h-14 bg-[#1e3a5f] hover:bg-[#2d4a6f] text-lg"
          >
            {uploading ? (
              <>
                <RefreshCw className="h-6 w-6 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Camera className="h-6 w-6 mr-2" />
                Prendre Photo
              </>
            )}
          </Button>
          
          {/* Gallery button */}
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="h-14 px-4"
          >
            <Upload className="h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
